import React, { useMemo, useState } from "react";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassFullIcon from "@mui/icons-material/HourglassFull";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PeopleIcon from "@mui/icons-material/People";
import { green, orange, red } from "@mui/material/colors";

import { AppState } from "../reducers";
import { parseDate, parseRelativeDate } from "../helpers/parser";
import EventForm from "./components/EventForm";
import NumberCard from "./components/NumberCard";
import AddAttendeeDialog from "./components/AddAttendeeDialog";
import SendFinalReminderDialog from "./components/SendFinalReminderDialog";
import {
  ATTENDEES_COLLECTION,
  ATTENDEES_STORE_KEY,
  AttendeeStatus,
  EVENTS_COLLECTION,
  summarizeAttendees,
} from "./shared";

const formatParticipants = (attendee: Attendee) =>
  attendee.status === AttendeeStatus.Attending ? attendee.participants : "-";

const formatCreatedAt = (createdAt: Attendee["createdAt"]) =>
  createdAt ? `${parseDate(createdAt, "MMM Do")} (${parseRelativeDate(createdAt)})` : "-";

const formatStatus = (
  status: Attendee["status"] = AttendeeStatus.Unconfirmed as Attendee["status"]
): string => {
  switch (status) {
    case AttendeeStatus.Attending:
      return "🟢";
    case AttendeeStatus.NotAttending:
      return "🔴";
    case AttendeeStatus.Unconfirmed:
    default:
      return "🟠";
  }
};

const formatEmailSent = (sent?: boolean) => (sent ? "🟢" : "🔴");

const columns: GridColDef<Attendee>[] = [
  { field: "name", headerName: "Navn", width: 250 },
  { field: "email", headerName: "Email", width: 250 },
  {
    field: "participants",
    headerName: "Antal Deltagere",
    width: 150,
    valueGetter: (_, row) => formatParticipants(row),
  },
  {
    field: "createdAt",
    headerName: "Dato for tilmelding",
    width: 200,
    valueGetter: (_, row) => formatCreatedAt(row.createdAt),
  },
  {
    field: "status",
    headerName: "Status",
    width: 130,
    valueGetter: (_, row) => formatStatus(row.status),
  },
  {
    field: "emailSent",
    headerName: "Deltager email afsendt",
    width: 195,
    valueGetter: (_, row) => formatEmailSent(row.emailSent),
  },
  {
    field: "finalReminderSent",
    headerName: "Bekræftelses email afsendt",
    width: 220,
    valueGetter: (_, row) => formatEmailSent(row.finalReminderSent),
  },
  {
    field: "followUpEmailSent",
    headerName: "Opfølgningsmail afsendt",
    width: 210,
    valueGetter: (_, row) => formatEmailSent(row.followUpEmailSent),
  },
];

const EventComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [addAttendeeOpen, setAddAttendeeOpen] = useState(false);
  const [finalReminderOpen, setFinalReminderOpen] = useState(false);

  useFirestoreConnect([
    {
      collection: EVENTS_COLLECTION,
      doc: id,
    },
    {
      collection: EVENTS_COLLECTION,
      doc: id,
      subcollections: [{ collection: ATTENDEES_COLLECTION }],
      storeAs: ATTENDEES_STORE_KEY,
    },
  ]);

  const event = useSelector(
    ({ firestore: { data } }: AppState) =>
      data[EVENTS_COLLECTION]?.[id] as InfoEvent | undefined
  );

  const attendees = useSelector(
    ({ firestore: { ordered } }: AppState) =>
      (ordered[ATTENDEES_STORE_KEY] as Attendee[]) || []
  );

  const attendeeSummary = useMemo(
    () => summarizeAttendees(attendees),
    [attendees]
  );

  const firestore = useFirestore();
  const attendeesRef = firestore
    .collection(EVENTS_COLLECTION)
    .doc(id)
    .collection(ATTENDEES_COLLECTION);

  const runSelectionBatch = async (
    confirmationMessage: string,
    applyChange: (batch: any, attendeeId: string) => void
  ) => {
    if (selection.length === 0 || !window.confirm(confirmationMessage)) {
      return;
    }

    try {
      const batch = firestore.batch();

      selection.forEach((attendeeId) => {
        applyChange(batch, attendeeId.toString());
      });

      await batch.commit();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} lg={10}>
        <Paper elevation={6} style={{ padding: 16 }}>
          <AddAttendeeDialog
            open={addAttendeeOpen}
            handleClose={() => setAddAttendeeOpen(false)}
            eventID={id}
          />
          <SendFinalReminderDialog
            open={finalReminderOpen}
            handleClose={() => setFinalReminderOpen(false)}
            eventID={id}
          />
          {event && <EventForm event={event} />}
          <Grid container spacing={3} item xs={12} style={{ marginTop: 24 }}>
            <Grid item xs>
              <NumberCard
                count={attendeeSummary.total}
                title="Antal tilmeldte"
                icon={<PeopleIcon />}
              />
            </Grid>
            <Grid item xs>
              <NumberCard
                count={attendeeSummary.unconfirmed}
                title="Afventer svar"
                icon={<HourglassFullIcon style={{ color: orange[500] }} />}
              />
            </Grid>
            <Grid item xs>
              <NumberCard
                count={attendeeSummary.notAttending}
                title="Afmeldte"
                icon={<CancelIcon style={{ color: red[500] }} />}
              />
            </Grid>
            <Grid item xs>
              <NumberCard
                count={attendeeSummary.attending}
                title="Kommer"
                icon={<CheckBoxIcon style={{ color: green[500] }} />}
              />
            </Grid>
            <Grid item xs>
              {event?.completed && (
                <NumberCard
                  count={event.attendance || 0}
                  title="Mødte op"
                  icon={<EventAvailableIcon style={{ color: green[500] }} />}
                />
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 24 }}>
            {event && (
              <ButtonGroup
                color="primary"
                aria-label="contained button group"
                style={{ marginBottom: 8 }}
              >
                <Button
                  disabled={event.completed || selection.length === 0}
                  color="secondary"
                  onClick={() =>
                    runSelectionBatch(
                      `Er du sikker på du vil slette ${selection.length} deltagere?`,
                      (batch, attendeeId) =>
                        batch.delete(attendeesRef.doc(attendeeId))
                    )
                  }
                >
                  Slet
                </Button>
                <Button
                  disabled={event.completed || selection.length === 0}
                  onClick={() =>
                    runSelectionBatch(
                      `Er du sikker på du vil sende emails til ${selection.length} deltagere?`,
                      (batch, attendeeId) =>
                        batch.update(attendeesRef.doc(attendeeId), {
                          shouldSendConfirmation: true,
                        })
                    )
                  }
                >
                  Send deltager emails
                </Button>
                <Button
                  disabled={event.completed}
                  onClick={() => setFinalReminderOpen(true)}
                >
                  Send bekræftelsesemails
                </Button>
                <Button
                  disabled={event.completed || selection.length === 0}
                  onClick={() =>
                    runSelectionBatch(
                      `Er du sikker på du vil sende opfølgnings emails til ${selection.length} deltagere?`,
                      (batch, attendeeId) =>
                        batch.update(attendeesRef.doc(attendeeId), {
                          shouldSendFollowUp: true,
                        })
                    )
                  }
                >
                  Send Opfølgningsmail
                </Button>
                <Button onClick={() => setAddAttendeeOpen(true)}>Tilføj</Button>
              </ButtonGroup>
            )}
          </Grid>
          <Grid item xs={12}>
            <div style={{ minHeight: 1300 }}>
              <DataGrid
                autoHeight
                rows={attendees}
                columns={columns}
                pageSizeOptions={[20, 50, 100]}
                checkboxSelection
                onRowSelectionModelChange={setSelection}
              />
            </div>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EventComponent;
